import { useCallback, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import EquitySimulatorGrowthChart from "@/components/dashboard/trading-tools/EquitySimulatorGrowthChart";
import TradingToolRangeSlider from "@/components/dashboard/trading-tools/TradingToolRangeSlider";
import {
  formatGainPercent,
  formatUsd,
  simulateEquityGrowth,
} from "@/data/equitySimulator";
import type { EquitySimulationResult, GrowthBarPoint } from "@/types/tradingTool";
import { cn } from "@/utils/utils";

const FIELD_CLASS =
  "support-contact-field w-full rounded-[8px] border border-white/10 bg-[#121212] px-3 py-2.5 font-neue text-sm text-white placeholder:text-white/40 focus:outline-none";

const TRADE_PRESETS = [25, 50, 100, 200] as const;

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block font-neue text-sm text-white/80">
      {children}
    </label>
  );
}

function ResultMetric({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <p className="font-neue text-xs text-white/80 md:text-sm">{label}</p>
      <p className="mt-1 font-neue text-xl font-medium text-white md:text-2xl">
        {value}
      </p>
    </div>
  );
}

export default function EquitySimulatorView() {
  const { t } = useTranslation();
  const [riskPercent, setRiskPercent] = useState("1");
  const [winRate, setWinRate] = useState(35);
  const [numTrades, setNumTrades] = useState("100");
  const [rewardRatio, setRewardRatio] = useState(2);
  const [results, setResults] = useState<EquitySimulationResult | null>(null);
  const [selectedBar, setSelectedBar] = useState<GrowthBarPoint | null>(null);

  const handleBarSelect = useCallback((bar: GrowthBarPoint) => {
    setSelectedBar(bar);
  }, []);

  const parsedTrades = Number(numTrades);

  const handleSimulate = () => {
    const nextResults = simulateEquityGrowth({
      riskPercent: Number(riskPercent),
      winRatePercent: winRate,
      rewardRatio,
      numTrades: parsedTrades,
    });

    setSelectedBar(null);
    setResults(nextResults);
  };

  const chartBars = useMemo(
    () => results?.chartBars ?? [],
    [results],
  );

  const displayedBalance =
    selectedBar?.value ?? results?.projectedBalance ?? null;
  const displayedGain =
    results && displayedBalance != null && results.startingBalance > 0
      ? ((displayedBalance - results.startingBalance) /
          results.startingBalance) *
        100
      : null;

  return (
    <div className="min-h-full w-full overflow-y-auto hide-scrollbar bg-[#050505] p-5">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="card-gradient-hover !block rounded-2xl p-[1.5px]">
          <div className="rounded-[14px] bg-[#0b0b0b] h-full p-5 md:p-6">
            <div className="space-y-5">
              <div>
                <FieldLabel>{t("Risk per Trade")}</FieldLabel>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={riskPercent}
                    onChange={(event) => {
                      setRiskPercent(event.target.value);
                      setResults(null);
                    }}
                    placeholder={t("Risk per Trade")}
                    className={cn(FIELD_CLASS, "pr-24")}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-neue text-xs uppercase tracking-wide text-white/35">
                    {t("Percent")}
                  </span>
                </div>
              </div>

              <div>
                <FieldLabel>{t("Win Rate")}</FieldLabel>
                <TradingToolRangeSlider
                  min={0}
                  max={100}
                  step={1}
                  value={winRate}
                  onChange={(value) => {
                    setWinRate(value);
                    setResults(null);
                  }}
                  formatValue={(value) => `${value}%`}
                />
              </div>

              <div>
                <FieldLabel>{t("Number of trades")}</FieldLabel>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={numTrades}
                  onChange={(event) => {
                    setNumTrades(event.target.value);
                    setResults(null);
                  }}
                  placeholder={t("Number of trades")}
                  className={`${FIELD_CLASS} !bg-white/5`}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {TRADE_PRESETS.map((preset) => {
                    const active = parsedTrades === preset;
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setNumTrades(String(preset));
                          setResults(null);
                        }}
                        className={cn(
                          "rounded-md border px-3 py-1.5 font-neue text-xs transition-colors sm:text-sm",
                          active
                            ? "btn-gradient-left border-transparent text-white"
                            : "border-white/10 bg-white/5 text-white/70 hover:border-white/25 hover:text-white",
                        )}
                      >
                        {t("{{count}} trades", { count: preset })}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="!mb-2">
                <FieldLabel>{t("Reward Ratio (R:R)")}</FieldLabel>
                <TradingToolRangeSlider
                  min={1}
                  max={5}
                  step={0.5}
                  value={rewardRatio}
                  onChange={(value) => {
                    setRewardRatio(value);
                    setResults(null);
                  }}
                  formatValue={(value) =>
                    Number.isInteger(value) ? `1:${value}` : `1:${value.toFixed(1)}`
                  }
                />
              </div>

              <button
                type="button"
                onClick={handleSimulate}
                className="btn-gradient-left  flex w-full items-center justify-center gap-2 rounded-[10px] px-4 py-2 font-neue text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                {t("Simulate my Growth")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </section>

        <section className="card-gradient !block rounded-2xl p-[1.5px]">
          <div className="relative overflow-hidden rounded-[14px]  h-full p-5 md:p-6">
            <div className="relative">
              <h2 className="font-neue text-base font-medium text-white md:text-lg mb-9">
                {t("Projected Growth")}
              </h2>

              <EquitySimulatorGrowthChart
                bars={chartBars}
                onSelectBar={handleBarSelect}
              />

              <div className="mt-6 grid grid-cols-1 gap-5 border-t border-white/10 pt-5 sm:grid-cols-2">
                <ResultMetric
                  label={t("Projected balance")}
                  value={
                    displayedBalance != null ? formatUsd(displayedBalance) : "—"
                  }
                />
                <ResultMetric
                  label={t("Expected gain")}
                  value={
                    displayedGain != null
                      ? formatGainPercent(displayedGain)
                      : "—"
                  }
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
